/**
 * PostDonationModal — the moment after Stripe confirms a gift.
 *
 * Michael, Mar 2026:
 *   "The moment someone gives, don't leave them alone with a
 *    receipt. Carry the light forward — invite them into the
 *    next Global Alignment. That's where the gift becomes shared."
 *
 * Behaviour:
 *   • Mounted by OneMillionLightsPage right after a paid checkout
 *     lands back on the site (state → 'illumination').
 *   • Only shown once per light: dismissal is remembered in
 *     localStorage keyed by the light id.
 *   • The "Count me in" CTA reuses the SAME public endpoint the
 *     GlobalAlignmentCard uses — so a single opt-in flows into the
 *     same weekly count.
 *   • Free-forever, never gated. No credit-card, no login.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const TOK = {
  cream: "#f5ecd9",
  creamReadable: "rgba(245,236,217,0.92)",
  creamDim: "rgba(245,236,217,0.72)",
  creamMist: "rgba(245,236,217,0.42)",
  gold: "#e0a97a",
  goldSoft: "rgba(224,169,122,0.92)",
  goldMist: "rgba(224,169,122,0.22)",
};

function fmtCountdown(ms) {
  if (ms <= 0) return null;
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${ss}s`;
  return `${m}m ${ss}s`;
}

function fmtLocalMoment(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "long", hour: "numeric", minute: "2-digit",
      timeZoneName: "short",
    });
  } catch (_) { return iso; }
}

export default function PostDonationModal({ open, light, onClose }) {
  const [alignment, setAlignment] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);

  // Pull next Global Alignment moment only while modal is open.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/one-million-lights/global-alignment/next`);
        if (!cancelled) setAlignment(r.data);
      } catch (_) { /* silent — the modal still degrades gracefully */ }
    })();
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => { cancelled = true; clearInterval(tick); };
  }, [open]);

  // If the visitor already opted in for this exact alignment, keep
  // the button in the sealed state so we don't double-count.
  useEffect(() => {
    if (!alignment?.alignment_key) return;
    try {
      const raw = localStorage.getItem("oml_alignment_joined");
      if (raw && JSON.parse(raw).key === alignment.alignment_key) setJoined(true);
    } catch (_) {}
  }, [alignment]);

  const countdown = useMemo(() => {
    if (!alignment?.next_at_utc) return null;
    return fmtCountdown(new Date(alignment.next_at_utc).getTime() - now);
  }, [alignment, now]);

  if (!open) return null;

  const joinAlignment = async () => {
    if (joining || joined) return;
    setJoining(true);
    try {
      let vid = null, email = null;
      try {
        vid = localStorage.getItem("oml_visitor_id");
        const fl = JSON.parse(localStorage.getItem("oml_founding_light") || "null");
        email = fl?.email || light?.email || null;
      } catch (_) {}
      const r = await axios.post(
        `${API}/api/one-million-lights/global-alignment/count-me-in`,
        { email, visitor_id: vid || `anon-${Date.now()}` },
      );
      setJoined(true);
      setAlignment((d) => d ? { ...d, count: r.data.count } : d);
      try {
        localStorage.setItem("oml_alignment_joined",
          JSON.stringify({ key: alignment?.alignment_key, at: new Date().toISOString() }));
      } catch (_) {}
    } catch (_) {
      /* silent — we don't want to interrupt the gratitude moment */
    } finally {
      setJoining(false);
    }
  };

  const dismiss = () => {
    // Remember dismissal so this modal only appears once per light.
    try {
      if (light?.id) {
        localStorage.setItem(`oml_postdonation_dismissed_${light.id}`, new Date().toISOString());
      }
    } catch (_) {}
    onClose && onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-5 py-8"
      role="dialog"
      aria-modal="true"
      data-testid="oml-postdonation-modal"
      style={{
        background: "radial-gradient(ellipse at 50% 30%, rgba(19,31,56,0.88) 0%, rgba(3,7,16,0.96) 60%, rgba(3,7,16,0.99) 100%)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        animation: "omlFadeIn 900ms ease",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div
        className="w-full max-w-md rounded-3xl p-7 text-center relative overflow-hidden"
        style={{
          background: "linear-gradient(180deg, rgba(224,169,122,0.14) 0%, rgba(19,31,56,0.4) 100%)",
          border: `1px solid ${TOK.gold}`,
          boxShadow: "0 40px 100px rgba(0,0,0,0.6), 0 0 60px rgba(224,169,122,0.22)",
        }}
      >
        {/* Soft interior halo */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(circle at 50% 8%, rgba(224,169,122,0.18) 0%, transparent 55%)",
          }}
        />

        <div className="relative">
          <div
            className="uppercase"
            style={{
              color: TOK.goldSoft,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.6rem",
              letterSpacing: "0.36em",
            }}
          >
            ✦  Thank you  ✦
          </div>

          <h3
            className="mt-4 italic leading-tight"
            style={{
              color: TOK.cream,
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "clamp(1.6rem, 5.4vw, 2.1rem)",
              fontWeight: 300,
            }}
            data-testid="oml-postdonation-heading"
          >
            Your light does not end here.
          </h3>

          <p
            className="mt-4 italic leading-relaxed max-w-sm mx-auto"
            style={{
              color: TOK.creamReadable,
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "clamp(1rem, 3.2vw, 1.15rem)",
            }}
          >
            {light?.intention ? (
              <>
                You placed <span style={{ color: TOK.gold }}>{light.intention}</span> into the world.
                {" "}Now carry it forward — join the next{" "}
                <span style={{ color: TOK.gold }}>Global Alignment</span>.
              </>
            ) : (
              <>
                Now carry it forward — join the next{" "}
                <span style={{ color: TOK.gold }}>Global Alignment</span>.
              </>
            )}
          </p>

          <p
            className="mt-4 italic leading-relaxed max-w-sm mx-auto"
            style={{
              color: TOK.creamDim,
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "clamp(0.95rem, 3vw, 1.05rem)",
            }}
          >
            A three-minute shared moment connecting people across
            countries, cultures and beliefs — one simple intention,
            free forever.
          </p>

          {alignment && (
            <div className="mt-6 grid grid-cols-2 gap-3 max-w-sm mx-auto">
              <div
                className="rounded-2xl p-3"
                style={{
                  background: "rgba(224,169,122,0.08)",
                  border: `1px solid ${TOK.goldMist}`,
                }}
                data-testid="oml-postdonation-next-moment"
              >
                <div
                  className="text-[0.52rem] uppercase tracking-[0.28em]"
                  style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
                >
                  Next moment
                </div>
                <div
                  className="mt-1 italic"
                  style={{
                    color: TOK.cream,
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: "0.95rem",
                  }}
                >
                  {fmtLocalMoment(alignment.next_at_utc)}
                </div>
              </div>
              <div
                className="rounded-2xl p-3"
                style={{
                  background: "rgba(224,169,122,0.08)",
                  border: `1px solid ${TOK.goldMist}`,
                }}
                data-testid="oml-postdonation-countdown"
              >
                <div
                  className="text-[0.52rem] uppercase tracking-[0.28em]"
                  style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
                >
                  {alignment.happening_now ? "Happening now" : "Begins in"}
                </div>
                <div
                  className="mt-1 italic"
                  style={{
                    color: TOK.cream,
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: "0.95rem",
                  }}
                >
                  {alignment.happening_now ? "Live" : countdown || "…"}
                </div>
              </div>
            </div>
          )}

          <button
            onClick={joinAlignment}
            disabled={joining || joined}
            data-testid="oml-postdonation-count-me-in"
            className="mt-7 rounded-full w-full"
            style={{
              background: joined
                ? "rgba(224,169,122,0.16)"
                : "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
              border: joined ? `1px solid ${TOK.gold}` : "1px solid #f5e0c5",
              color: joined ? TOK.gold : "#1a0f05",
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.86rem",
              fontWeight: 700,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              padding: "16px 28px",
              boxShadow: joined ? "none" : "0 0 30px rgba(224,169,122,0.35)",
              opacity: joining ? 0.75 : 1,
              cursor: joining || joined ? "default" : "pointer",
            }}
          >
            {joined ? "✓  You're in this Sunday" : joining ? "Joining…" : "Count me in"}
          </button>

          {alignment?.count ? (
            <div
              className="mt-4 text-[0.7rem] uppercase tracking-[0.28em]"
              style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
              data-testid="oml-postdonation-count"
            >
              {Number(alignment.count || 0).toLocaleString("en-GB")} joining · Every light welcome
            </div>
          ) : null}

          <button
            type="button"
            onClick={dismiss}
            data-testid="oml-postdonation-close"
            className="mt-5 text-[0.7rem] uppercase tracking-[0.24em]"
            style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}
          >
            {joined ? "Return to my light" : "Not right now"}
          </button>

          <p
            className="mt-5 italic max-w-sm mx-auto text-[0.85rem] leading-relaxed"
            style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif' }}
          >
            No comments · No debate · No news. Just three minutes,
            one intention, every belief welcome.
          </p>
        </div>
      </div>
    </div>
  );
}
